import type { ReactElement } from 'react';

import { ControlledLolChampionInput } from 'app/pages/MatchPage/Microbetting/LolChampionInput';
import { ControlledTimerDurationDropdownInput } from 'app/pages/MatchPage/Microbetting/TimerDurationDropdownInput';
import { ControlledLolEpicMonsterIndexInput } from 'app/pages/MatchPage/Microbetting/LolEpicMonsterIndexInput';
import type { TraderInputsContextValue } from 'app/components/MatchScoreboard/TraderInputsContext/types';
import { ControlledTimerStartEndDropdownInput } from 'app/pages/MatchPage/Microbetting/TimerStartEndDropdownInput';

const VIDEOGAME_WITH_MICROBETTING: VideogameSlug[] = ['league-of-legends'];

interface DurationTraderInput {
  start: number;
  end: number;
}

interface GetNameFunctionArguments {
  market: MicroMarket;
  traderInputs: TraderInputItem[];
  pushUpdate: TraderInputsContextValue['pushUpdate'];
  disabled: TraderInputsContextValue['micro_locked'];
  traderInputType?: 'input' | 'display' | 'bookmaker_display' | undefined;
}

interface PendingTraderInputComponentProps {
  disabled: boolean;
  onChange: (val: number | DurationTraderInput | null) => void;
  value: number | DurationTraderInput | null;
  onlyLabel: boolean;
  replacedTextMatch?: string;
}

interface MicromarketConfig {
  baseTimer?: number;
  getFormatNameComponent?: (props: PendingTraderInputComponentProps) => ReactElement;
  getLineValue?: (
    market: MicroMarket,
    traderInputs: TraderInputItem[],
    value: any
  ) => string | null;
  traderInput: {
    category: TraderInputItem['category'];
    textToReplace?: string[];
    stagedType?: TraderInputItem['type'];
    pendingType?: TraderInputItem['type'];
  };
}

const MICROMARKET_DEFAULT_TIMER_IN_S = 30;

const MICROBETTING_LANE_OPTIONS: string[] = ['lane-top', 'lane-middle', 'lane-bottom', 'none'];

const MICROMARKETS: Record<MicroMarketTemplate, MicromarketConfig> = {
  'micro-champion-picked-or-banned': {
    baseTimer: 45,
    getFormatNameComponent: props => (
      <ControlledLolChampionInput {...props} value={props.value as number | null} />
    ),
    traderInput: {
      textToReplace: ['Champion'],
      category: 'micro_draft',
      pendingType: 'champion_picked_or_banned_selected'
    }
  },
  'micro-drake-slain': {
    getFormatNameComponent: props => (
      <ControlledLolEpicMonsterIndexInput
        total={7}
        {...props}
        value={props.value as number | null}
      />
    ),
    traderInput: {
      category: 'micro_drake',
      stagedType: 'supremacy',
      pendingType: 'index'
    }
  },
  'micro-kill-most-2-way-between-time-window': {
    getFormatNameComponent: props => (
      <ControlledTimerStartEndDropdownInput
        {...props}
        value={props.value as DurationTraderInput | null}
      />
    ),
    traderInput: {
      textToReplace: ['StartTime', 'EndTime'],
      category: 'micro_kill',
      stagedType: 'supremacy_between_time_window',
      pendingType: 'time_window'
    }
  },
  'micro-kill-first': {
    baseTimer: 45,
    traderInput: {
      category: 'micro_kill',
      stagedType: 'first_supremacy'
    }
  },
  'micro-drake-stolen': {
    baseTimer: 20,
    getFormatNameComponent: props => (
      <ControlledLolEpicMonsterIndexInput
        total={7}
        {...props}
        value={props.value as number | null}
      />
    ),
    traderInput: {
      category: 'micro_drake',
      pendingType: 'index',
      stagedType: 'stolen_supremacy'
    }
  },
  'micro-nashor-stolen': {
    baseTimer: 20,
    getFormatNameComponent: props => (
      <ControlledLolEpicMonsterIndexInput
        total={7}
        {...props}
        value={props.value as number | null}
      />
    ),
    traderInput: {
      category: 'micro_nashor',
      pendingType: 'index',
      stagedType: 'stolen_supremacy'
    }
  },
  'micro-game-end-before-timer': {
    getFormatNameComponent: props => (
      <ControlledTimerDurationDropdownInput
        title="component-timer_start_end_dropdown-end"
        type="timer"
        example="11:00"
        {...props}
        value={props.value as number | null}
      />
    ),
    traderInput: {
      textToReplace: ['Time'],
      category: 'micro_game',
      pendingType: 'timer_before_game_end',
      stagedType: 'before_game_end_totals'
    }
  },
  'micro-rift-herald-summoned-lane': {
    getFormatNameComponent: props => (
      <ControlledLolEpicMonsterIndexInput
        total={2}
        {...props}
        value={props.value as number | null}
      />
    ),
    traderInput: {
      category: 'micro_rift_herald',
      stagedType: 'summoned_lane_selected',
      pendingType: 'index'
    }
  },
  'micro-tower-destroyed': {
    getFormatNameComponent: props => (
      <ControlledLolEpicMonsterIndexInput
        total={22}
        {...props}
        value={props.value as number | null}
      />
    ),
    traderInput: {
      category: 'micro_tower',
      stagedType: 'supremacy',
      pendingType: 'index'
    }
  },
  'micro-nashor-slain': {
    getFormatNameComponent: props => (
      <ControlledLolEpicMonsterIndexInput
        total={6}
        {...props}
        value={props.value as number | null}
      />
    ),
    traderInput: {
      category: 'micro_nashor',
      stagedType: 'supremacy',
      pendingType: 'index'
    }
  },
  'micro-inhibitor-first': {
    traderInput: {
      category: 'micro_inhibitor',
      stagedType: 'first_supremacy'
    }
  }
};

export {
  MICROMARKETS,
  MICROMARKET_DEFAULT_TIMER_IN_S,
  MICROBETTING_LANE_OPTIONS,
  VIDEOGAME_WITH_MICROBETTING
};
export type {
  MicromarketConfig,
  DurationTraderInput,
  GetNameFunctionArguments,
  PendingTraderInputComponentProps
};
